import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { ChevronLeft, ChevronDown, Search } from 'lucide-react';
import { toast } from 'sonner';
import { FieldLabel, TextField, BrandSpinner } from '@scripsteam/scrips-signal-ds';
import { DatePickerField } from '@/shared/ui/components';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useAuthStore } from '@/auth/auth-store';
import {
  useCreatePatient,
  type CreatePatientInput,
} from '@/patients/services/use-create-patient';
import { useGenderList } from '@/billing/services/use-genders';
import { useValidatePhoneNumber } from '@/patients/services/use-validate-phone-number';
import {
  usePreBookSelectedPatient,
  toInvoicePrices,
} from '@/services/scheduling/use-prebook-selected-patient';
import type { AppointmentFormValues } from '@/types/appointment-form';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

/**
 * CreatePatientView — MVP inline Create Patient flow opened from the
 * Patient section of the create-appointment panel.
 *
 * Flutter parity: simplified version of `create_patient_screen.dart`.
 * Captures Tab 0's required fields only — name, gender, DOB, email,
 * phone — POSTs to /api/Patients, then back-fills the appointment
 * form with the new patient and returns to the panel.
 *
 * Out of scope (tracked separately on PROD-709):
 *   - Contact Details tab (residential address, secondary contact)
 *   - Matching-records guard (3 debounced lookups)
 *   - Emirates ID SignalR auto-fill (PROD-710)
 *   - Patient photo upload
 *   - Marital status / occupation / education / nationality / ID type
 */

interface CreatePatientViewProps {
  onClose: () => void;
  organizationId: string;
  practiceId: string;
}

interface DraftPatient {
  firstName: string;
  middleName: string;
  lastName: string;
  /** Master-data gender GUID — backend rejects anything else with
   *  "Error converting value to System.Guid". Empty string means
   *  "not yet picked". */
  genderId: string;
  /** Display label captured at pick time so we can back-fill the
   *  appointment form's `patientGender` (a string) without having
   *  to lookup again. */
  genderName: string;
  birthDate: string; // YYYY-MM-DD from <input type="date">
  email: string;
  /** Local part of the phone (no country code). */
  phoneLocal: string;
}

const INITIAL_DRAFT: DraftPatient = {
  firstName: '',
  middleName: '',
  lastName: '',
  genderId: '',
  genderName: '',
  birthDate: '',
  email: '',
  phoneLocal: '',
};

interface CountryEntry {
  iso2: string;
  dial: string;
  name: string;
}
/** Country dial-code list — same set as `create-contact-view.tsx:22`.
 *  Flutter parity for the picker (GCC + IN + UM/CA/US + tail). Shared
 *  list is a follow-up extraction; duplicated here for now. */
const COUNTRIES: CountryEntry[] = [
  { iso2: 'ae', dial: '+971', name: 'United Arab Emirates' },
  { iso2: 'eg', dial: '+20', name: 'Egypt' },
  { iso2: 'qa', dial: '+974', name: 'Qatar' },
  { iso2: 'sa', dial: '+966', name: 'Saudi Arabia' },
  { iso2: 'om', dial: '+968', name: 'Oman' },
  { iso2: 'kw', dial: '+965', name: 'Kuwait' },
  { iso2: 'bh', dial: '+973', name: 'Bahrain' },
  { iso2: 'in', dial: '+91', name: 'India' },
  { iso2: 'ca', dial: '+1', name: 'Canada' },
  { iso2: 'us', dial: '+1', name: 'United States' },
  { iso2: 'gb', dial: '+44', name: 'United Kingdom' },
  { iso2: 'pk', dial: '+92', name: 'Pakistan' },
  { iso2: 'ph', dial: '+63', name: 'Philippines' },
  { iso2: 'ng', dial: '+234', name: 'Nigeria' },
  { iso2: 'jo', dial: '+962', name: 'Jordan' },
  { iso2: 'lb', dial: '+961', name: 'Lebanon' },
];

/** flagcdn.com flag image — Windows doesn't render emoji flags so we use
 *  real PNGs here (mirrors `create-contact-view.tsx:41`). */
function flagSrc(iso2: string): string {
  return `https://flagcdn.com/24x18/${iso2.toLowerCase()}.png`;
}

interface CountryPickerProps {
  selectedDial: string;
  search: string;
  onSearchChange: (next: string) => void;
  onSelect: (country: CountryEntry) => void;
  onClose: () => void;
}

/** CountryPicker — same as create-contact-view's picker. Dropdown beneath
 *  the country trigger, filters by ISO2 / dial / name; backdrop dismisses
 *  without selecting. */
function CountryPicker({
  selectedDial,
  search,
  onSearchChange,
  onSelect,
  onClose,
}: CountryPickerProps) {
  const q = search.trim().toLowerCase();
  const visible = q
    ? COUNTRIES.filter(
        (c) =>
          c.iso2.includes(q) ||
          c.dial.toLowerCase().includes(q) ||
          c.name.toLowerCase().includes(q),
      )
    : COUNTRIES;

  return (
    <>
      <button
        type="button"
        aria-label="Close country picker"
        onClick={onClose}
        className="fixed inset-0 z-10 cursor-default"
      />
      <div className="absolute z-20 left-0 top-full mt-1 w-65 rounded-md border border-border-default bg-surface-card shadow-6 overflow-hidden">
        <div className="p-2 border-b border-border-subtle">
          <div className="relative">
            <Search className="absolute left-3 top-3 size-4 text-text-hint pointer-events-none" />
            <input
              autoFocus
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search country"
              className="w-full h-10 pl-8 pr-3 rounded-md bg-grey-100 text-sm text-text-primary placeholder:text-text-hint focus:bg-surface-card focus:outline-none focus:ring-1 focus:ring-brand-primary"
            />
          </div>
        </div>
        <div className="max-h-70 overflow-y-auto scrollbar-hide py-1">
          {visible.length === 0 ? (
            <p className="text-sm text-text-muted text-center py-4">
              No matching countries.
            </p>
          ) : (
            visible.map((c) => (
              <button
                key={`${c.iso2}-${c.dial}`}
                type="button"
                onClick={() => onSelect(c)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-grey-100',
                  c.dial === selectedDial ? 'bg-blue-100' : '',
                )}
              >
                <img
                  src={flagSrc(c.iso2)}
                  alt={c.iso2.toUpperCase()}
                  width={24}
                  height={18}
                  className="rounded-sm flex-shrink-0"
                  loading="lazy"
                />
                <span className="text-sm text-text-primary tabular-nums">
                  {c.dial}
                </span>
                <span className="text-xs text-text-muted ml-auto">
                  ({c.iso2.toUpperCase()})
                </span>
              </button>
            ))
          )}
        </div>
      </div>
    </>
  );
}

function isValidEmail(email: string): boolean {
  if (!email) return true; // optional
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function toLocalIsoDate(yyyymmdd: string): string {
  // <input type="date"> yields "YYYY-MM-DD"; backend wants .NET local
  // form. Append T00:00:00.000 (no Z) — matches the dates pattern in
  // the working insurance save curl.
  if (!yyyymmdd) return '';
  return `${yyyymmdd}T00:00:00.000`;
}

export function CreatePatientView({
  onClose,
  organizationId,
  practiceId,
}: CreatePatientViewProps) {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const providerId = watch('providerId') as string | null;
  const location = watch('location');
  const billingProfileId = watch('billingProfileId') as string | null;
  const auth = useAuthStore((s) => s.user);
  const orgId = organizationId || auth?.organizationId || '';
  const pracId = practiceId || auth?.practiceId || '';

  const [draft, setDraft] = useState<DraftPatient>(INITIAL_DRAFT);
  const [countryCode, setCountryCode] = useState('+971');
  const [countryIso2, setCountryIso2] = useState('ae');
  const [countryPickerOpen, setCountryPickerOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const createMutation = useCreatePatient();
  const preBookMutation = usePreBookSelectedPatient();
  const gendersQ = useGenderList();
  const genders = gendersQ.data ?? [];

  // Phone validation — Flutter parity (callback-contact pattern):
  //   - empty                  → no error, no API call
  //   - digits < 7             → "Please, provide a valid number" (no API)
  //   - digits >= 7 (debounced)→ server-side carrier check
  //     (POST /api/Master/ValidatePhoneNumber; format-only verdict when the
  //     carrier lookup is unavailable — see useValidatePhoneNumber)
  const phoneDigits = draft.phoneLocal.replace(/\D+/g, '');
  const e164 = `${countryCode}${phoneDigits}`;
  const debouncedLookup = useDebounce(e164, 500);
  const validatePhone = useValidatePhoneNumber();
  const [phoneError, setPhoneError] = useState<string | null>(null);

  useEffect(() => {
    if (phoneDigits.length === 0) {
      setPhoneError(null);
      return;
    }
    if (phoneDigits.length < 7) {
      setPhoneError('Please, provide a valid number');
      return;
    }
    setPhoneError(null);
    validatePhone.mutate(
      { contactNumber: debouncedLookup },
      {
        onSuccess: (result) => {
          setPhoneError(result.valid ? null : 'Please, provide a valid number');
        },
        onError: () => {
          // The check itself failed with a client/auth error (an
          // outage resolves to a format-only verdict instead): surface
          // a softer message but STILL hard-block save (phoneError
          // truthy → phoneOK false → canSave false).
          setPhoneError('Could not validate phone number');
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedLookup]);

  const requiredOK =
    !!draft.firstName.trim() &&
    !!draft.lastName.trim() &&
    !!draft.genderId &&
    !!draft.birthDate &&
    !!draft.phoneLocal.trim();
  const emailOK = isValidEmail(draft.email.trim());
  const phoneOK = !phoneError && !validatePhone.isPending;
  const canSave =
    requiredOK && emailOK && phoneOK && !createMutation.isPending;

  const update = <K extends keyof DraftPatient>(key: K, value: DraftPatient[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!canSave) return;
    const input: CreatePatientInput = {
      firstName: draft.firstName.trim(),
      middleName: draft.middleName.trim() || null,
      lastName: draft.lastName.trim(),
      gender: draft.genderId,
      genderName: { id: draft.genderId, name: draft.genderName },
      birthDate: toLocalIsoDate(draft.birthDate),
      email: draft.email.trim() || null,
      phoneNumber: `${countryCode}-${draft.phoneLocal.replace(/\D/g, '')}`,
      organizationId: orgId,
      practiceId: pracId,
      isAdult: true,
      isCreatedFromPatientMenu: false,
    };
    try {
      const result = await createMutation.mutateAsync(input);
      const newId = result.patientId;
      if (!newId) {
        toast.error('Patient created but no ID returned.');
        return;
      }
      // Back-fill the appointment form so the newly created patient
      // is selected when we return to the panel. The downstream
      // PatientSection consumer reads these flat fields for the
      // selected-state card render; PreBookSelectedPatient will fire
      // from the panel on next interaction to load the full profile +
      // prices.
      const fullName = [input.firstName, input.middleName, input.lastName]
        .filter(Boolean)
        .join(' ');
      // Match the patient-section selectPatient flow so the Book
      // button doesn't stay disabled. The two extras over the prior
      // version: (1) `shouldValidate` on patientId so RHF re-runs the
      // form-level superRefine, (2) `sponsorType` resets to Self-Pay
      // (Flutter create_appointment_widget.dart:1411) so the
      // sponsor-required gate clears for the newly-created patient.
      setValue('patientId', newId, { shouldValidate: true });
      setValue('patientName', fullName);
      setValue('patientDob', input.birthDate);
      setValue('patientGender', draft.genderName);
      setValue('patientIsMissingData', true);
      setValue('sponsorType', 'Self-Pay', { shouldValidate: true });

      // Mirror the patient-search PreBook fire so the new patient gets
      // their address list / residentialAddressId / invoice prices /
      // emergency-contact list populated — same gates as
      // patient-section.tsx:89 (provider + location set).
      //
      // **Sequencing note (review-flagged):** PreBook fires AFTER the
      // create mutation has resolved with a 200 + a real `patientId`
      // (the guard above bails to a toast if `newId` is missing). This
      // is strictly post-success — not optimistic. If `createMutation`
      // throws, control falls into the outer `catch` below and PreBook
      // is never called, so a failed save can't leave the panel
      // dangling on a fictional patient.
      if (providerId && location?.id) {
        try {
          const response = await preBookMutation.mutateAsync({
            patientId: newId,
            providerId,
            locationId: location.id,
            billingProfileId,
            paymentType: 'Self-pay',
          });
          // Empty-fullAddress filter — Flutter parity (see
          // `patient-section.tsx` for the matching note).
          const rawAddresses = response.patientAddressList ?? [];
          const addresses = rawAddresses.filter(
            (a) =>
              [a.apartment, a.address, a.city, a.country]
                .map((p) => (p ?? '').trim())
                .some((p) => p.length > 0),
          );
          setValue('addressList', addresses);
          const def = addresses.find((a) => a.isDefault) ?? addresses[0];
          const defId = def?.id ?? null;
          if (def) setValue('residentialAddress', def);
          if (defId)
            setValue('residentialAddressId', defId, { shouldValidate: true });
          const normalised = toInvoicePrices(response.prices);
          if (normalised) setValue('invoiceTotals', normalised);
        } catch {
          // Non-fatal — patient was created. PreBook errors surface via
          // the global toast handler; the panel will retry on next
          // interaction.
        }
      }

      toast.success(`Successfully added ${input.firstName}`);
      onClose();
    } catch {
      // Global toast handler surfaces the server error.
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="booking-subhead" data-slot="booking-subhead">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-1 -ml-1 p-1 text-brand-primary hover:text-interactive-hover"
          aria-label="Back"
        >
          <ChevronLeft className="size-4" />
          <span className="text-sm font-semibold">Back</span>
        </button>
        <h2 className="booking-subhead-title">
          Create Patient
        </h2>
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          className={cn(
            'text-sm font-semibold px-2',
            canSave
              ? 'text-brand-primary hover:text-interactive-hover'
              : 'text-text-hint cursor-not-allowed',
          )}
        >
          {createMutation.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide px-4 py-3">
        <p className="sched-sub-label" data-gap="wide">
          Patient Details
        </p>

        <div className="rounded-card border border-border-default bg-surface-card p-4 space-y-4">
          <div>
            <FieldLabel >
              First Name <span className="text-status-error">*</span>
            </FieldLabel>
            <TextField
              type="text"
              value={draft.firstName}
              onChange={(e) => update('firstName', e.target.value)}
              placeholder="Enter first name"
              className="mt-1"
            />
          </div>

          <div>
            <FieldLabel >
              Middle Name
            </FieldLabel>
            <TextField
              type="text"
              value={draft.middleName}
              onChange={(e) => update('middleName', e.target.value)}
              placeholder="Enter middle name (Optional)"
              className="mt-1"
            />
          </div>

          <div>
            <FieldLabel >
              Last Name <span className="text-status-error">*</span>
            </FieldLabel>
            <TextField
              type="text"
              value={draft.lastName}
              onChange={(e) => update('lastName', e.target.value)}
              placeholder="Enter last name"
              className="mt-1"
            />
          </div>

          <div>
            <FieldLabel >
              Gender <span className="text-status-error">*</span>
            </FieldLabel>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {gendersQ.isPending ? (
                <BrandSpinner size={20} label="Loading…" />
              ) : genders.length === 0 ? (
                <span className="text-sm text-text-muted">
                  No genders available
                </span>
              ) : (
                genders.map((g) => {
                  const id = g.id ?? '';
                  if (!id) return null;
                  const active = draft.genderId === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() =>
                        setDraft((prev) => ({
                          ...prev,
                          genderId: id,
                          genderName: g.name ?? '',
                        }))
                      }
                      className={cn(
                        'rounded-full px-4 py-1.5 text-sm font-semibold transition-colors',
                        active
                          ? 'bg-brand-primary text-white'
                          : 'bg-grey-100 text-brand-primary hover:bg-grey-200',
                      )}
                    >
                      {g.name}
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div>
            <FieldLabel >
              Date of Birth <span className="text-status-error">*</span>
            </FieldLabel>
            <DatePickerField
              value={draft.birthDate}
              onChange={(v) => update('birthDate', v)}
              max={new Date()}
              className="mt-1"
            />
          </div>

          <div>
            <FieldLabel >
              Email Address
            </FieldLabel>
            <TextField
              type="email"
              value={draft.email}
              onChange={(e) => update('email', e.target.value)}
              placeholder="Enter email address"
              className="mt-1"
            />
            {draft.email && !emailOK ? (
              <p className="mt-1 text-xs text-status-error">
                Enter a valid email address.
              </p>
            ) : null}
          </div>

          <div>
            <FieldLabel >
              Primary Contact Number <span className="text-status-error">*</span>
            </FieldLabel>
            <div className="mt-1 flex items-stretch gap-2 relative">
              <button
                type="button"
                onClick={() => {
                  setCountryPickerOpen((o) => !o);
                  setCountrySearch('');
                }}
                className="flex items-center gap-2 h-10 px-2 rounded-md bg-grey-100 hover:bg-grey-200"
              >
                <img
                  src={flagSrc(countryIso2)}
                  alt={countryIso2.toUpperCase()}
                  width={24}
                  height={18}
                  className="rounded-sm flex-shrink-0"
                  loading="lazy"
                />
                <span className="text-sm text-text-primary tabular-nums">
                  {countryCode}
                </span>
                <ChevronDown className="size-4 text-text-secondary" />
              </button>
              <input
                type="tel"
                inputMode="tel"
                value={draft.phoneLocal}
                onChange={(e) =>
                  update(
                    'phoneLocal',
                    e.target.value.replace(/[^\d\s]/g, '').slice(0, 20),
                  )
                }
                placeholder="e.g. 23 345 4567"
                className={cn(
                  'flex-1 h-10 px-3 rounded-md bg-grey-100 text-sm text-text-primary placeholder:text-text-hint focus:bg-surface-card focus:outline-none focus:ring-1',
                  phoneError ? 'focus:ring-status-error' : 'focus:ring-brand-primary',
                )}
              />
              {countryPickerOpen ? (
                <CountryPicker
                  selectedDial={countryCode}
                  search={countrySearch}
                  onSearchChange={setCountrySearch}
                  onSelect={(c) => {
                    setCountryCode(c.dial);
                    setCountryIso2(c.iso2);
                    setCountryPickerOpen(false);
                    setCountrySearch('');
                  }}
                  onClose={() => setCountryPickerOpen(false)}
                />
              ) : null}
            </div>
            {validatePhone.isPending ? (
              <p className="text-xs text-text-muted mt-1">Validating phone…</p>
            ) : phoneError ? (
              <p className="text-xs text-status-error mt-1">{phoneError}</p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
